import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth, useCopyToast, useIsMobile, useT } from '@repo/common';
import HeaderText from '../../components/MyPage/Default/HeaderText';
import MyProfileSidebar from '../../components/MyPage/Default/MyProfileSidebar';
import AccountSetting from '../../components/MyPage/AccountSetting/AccountSetting';
import MyProfile from '../../components/MyPage/MyProfile/MyProfile';
import MyApply from '../../components/MyPage/MyApply/MyApply';
import Payments from '../../components/MyPage/Payments/Payments';
import Activities from '../../components/MyPage/Activities/Activites';
import Coupons from '../../components/MyPage/Coupons/Coupons';
import WorkshopManagement from '../../components/MyPage/WorkshopManagement/WorkshopManagement';
import AdminPayments from '../../components/MyPage/AdminPayments/AdminPayments';
import AdminRefunds from '../../components/MyPage/AdminRefunds/AdminRefunds';
import UserManagement from '../../components/MyPage/UserManagement/UserManagement';
import AdminPolicyNotice from '../../components/MyPage/AdminPolicyNotice/AdminPolicyNotice';
import AdminPolicies from '../../components/MyPage/AdminPolicies/AdminPolicies';
import ViewInvoice from '../../components/MyPage/ViewInvoice/ViewInvoice';
import PersonalOnlyBlocked from '../../components/Common/PersonalOnlyBlocked';
import { uploadProfileImage } from '../../api/user/user.api';
import PersonalInfoProfileImage from '../../components/MyPage/MyProfile/PersonalInfo/PersonalInfoProfileImage';
import { ReactComponent as NavigationLeftIcon } from '@repo/common/assets/icons/20px/icon_20px_navigation-left.svg';
import { ReactComponent as LinksIcon } from '@repo/common/assets/icons/20px/icon_20px_links.svg';

const BUSINESS_ROLES = new Set([
  'E',
  'ROLE_E',
  'EMPLOYER',
  'ROLE_EMPLOYER',
  'EMPLOYEE',
  'ROLE_EMPLOYEE',
]);
const BUSINESS_TABS = new Set([
  'My Profile',
  'My Apply',
  'Account setting',
]);
// 개인 전용 탭: 비즈니스 계정이 들어오면 차단 상태 화면을 보여준다
const PERSONAL_ONLY_TABS = new Set(['Activities', 'Coupons']);
const BUSINESS_TAB_LABELS = {
  'My Profile': 'Profile',
  'My Apply': 'Jobs',
  'Account setting': 'Account settings',
};

const TAB_LABELS_KR = {
  'My Profile': '내 프로필',
  'My Apply': '내 지원',
  Activities: '활동',
  'Account setting': '계정 설정',
  Payments: '결제',
  Coupons: '쿠폰',
  Profile: '프로필',
  Jobs: '채용 공고',
  'Account settings': '계정 설정',
  'Workshop Management': '워크숍 관리',
  Applications: '지원 현황',
  'User Management': '회원 관리',
  'Refunds & Cancellations': '환불 및 취소',
  // 사이드바가 이 탭만 'refunds' 키로 보낸다 — 모바일 상단 제목용
  refunds: '환불 및 취소',
  'Policy Notice': '약관 개정 안내',
  Policies: '정책 관리',
};

const MyPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isLoggedIn } = useAuth();
  const isAdmin = user?.role === 'A';
  const isBusiness = BUSINESS_ROLES.has(
    String(user?.role ?? '').toUpperCase(),
  );
  const isMobile = useIsMobile();
  const t = useT();

  const [profileImageFile, setProfileImageFile] = useState(null);
  const { copy, copyToast } = useCopyToast();

  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/');
    }
  }, [isLoggedIn]);

  const getTabFromUrl = () => {
    const params = new URLSearchParams(location.search);
    return params.get('tab');
  };

  const [activeMenu, setActiveMenu] = useState(() => {
    const urlTab = getTabFromUrl();

    if (urlTab) return urlTab;

    const returnMenu = localStorage.getItem('authReturnMenu');
    if (returnMenu) {
      localStorage.removeItem('authReturnMenu');
      return returnMenu;
    }

    const savedMenu = localStorage.getItem('myPageActiveMenu');
    if (savedMenu) return savedMenu;

    return 'My Profile';
  });

  const invoiceId = new URLSearchParams(location.search).get('invoice');
  const isInvoice = invoiceId;

  const urlTab = getTabFromUrl();
  const isMobileDetail = isMobile && !!urlTab;
  const isMobileMenu = isMobile && !urlTab;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlTab = params.get('tab');

    if (urlTab && urlTab !== activeMenu) {
      setActiveMenu(urlTab);
    }
  }, [activeMenu, location.search]);

  useEffect(() => {
    localStorage.setItem('myPageActiveMenu', activeMenu);
  }, [activeMenu]);

  useEffect(() => {
    if (
      !isBusiness ||
      BUSINESS_TABS.has(activeMenu) ||
      PERSONAL_ONLY_TABS.has(activeMenu)
    ) {
      return;
    }

    setActiveMenu('My Profile');
    navigate('/mypage?tab=My%20Profile', { replace: true });
  }, [activeMenu, isBusiness, navigate]);

  const handleMenuClick = (menuName) => {
    navigate(`/mypage?tab=${encodeURIComponent(menuName)}`);
  };

  const handleSaveProfile = async (payload) => {
    try {
      await updateMyProfile(payload);
    } catch (error) {
      console.error('프로필 저장에 실패했습니다.', error);
    }
  };

  const handleBack = () => {
    if (invoiceId) {
      navigate('/mypage?tab=Payments');
    } else {
      navigate('/mypage');
    }
  };

  const handleCopyLink = () => copy(window.location.href);

  const renderContent = () => {
    if (isBusiness && PERSONAL_ONLY_TABS.has(activeMenu)) {
      return <PersonalOnlyBlocked inline />;
    }

    if (invoiceId) {
      return (
        <div className='tabletH:min-w-[428px] tabletH:max-w-[868px] w-full'>
          <ViewInvoice invoiceId={invoiceId} />
        </div>
      );
    }

    switch (activeMenu) {
      case 'My Profile':
        return (
          <div className='tabletH:min-w-[428px] tabletH:max-w-[568px] w-full'>
            <MyProfile />
          </div>
        );
      case 'My Apply':
        return (
          <div className='tabletH:min-w-[520px] w-full'>
            <MyApply />
          </div>
        );
      case 'Activities':
        return (
          <div className='tabletH:min-w-[480px] w-full'>
            <Activities />
          </div>
        );
      case 'Account setting':
        return (
          <div className='tabletH:max-w-[568px] w-full'>
            <AccountSetting />
          </div>
        );
      case 'Payments':
        return (
          <div className='tabletH:min-w-[790px] w-full'>
            <Payments />
          </div>
        );
      case 'Coupons':
        return (
          <div className='tabletH:min-w-[790px] w-full'>
            <Coupons />
          </div>
        );
      default:
        return null;
    }
  };

  const adminRenderContent = () => {
    if (invoiceId) {
      return (
        <div className='min-w-0 max-w-[868px] w-full'>
          <ViewInvoice invoiceId={invoiceId} />
        </div>
      );
    }

    switch (activeMenu) {
      case 'My Profile':
        return (
          <div className='min-w-[428px] max-w-[568px] w-full'>
            <MyProfile />
          </div>
        );
      case 'Workshop Management':
        return (
          <div className='min-w-0 max-w-[1164px] w-full'>
            <WorkshopManagement />
          </div>
        );
      case 'Applications':
        return (
          <div className='min-w-0 max-w-[1164px] w-full'>
            <MyApply />
          </div>
        );
      case 'User Management':
        return (
          <div className='min-w-0 max-w-[1164px] w-full'>
            <UserManagement />
          </div>
        );
      case 'Payments':
        return (
          <div className='min-w-0 max-w-[1164px] w-full'>
            <AdminPayments />
          </div>
        );
      // 사이드바는 이 메뉴만 tab 키를 'refunds' 로 보낸다 (다른 메뉴는 라벨 그대로).
      // 라벨만 받던 탓에 이 탭이 항상 빈 화면이었다 — 두 값을 모두 받는다.
      case 'refunds':
      case 'Refunds & Cancellations':
        return (
          <div className='min-w-0 max-w-[1164px] w-full'>
            <AdminRefunds />
          </div>
        );
      case 'Policies':
        return (
          <div className='min-w-0 max-w-[1164px] w-full'>
            <AdminPolicies />
          </div>
        );
      case 'Policy Notice':
        return (
          <div className='min-w-0 max-w-[1164px] w-full'>
            <AdminPolicyNotice />
          </div>
        );
      case 'Account setting':
        return (
          <div className='max-w-[568px] w-full'>
            <AccountSetting />
          </div>
        );
      case 'Coupons':
        return <div>Null</div>;
      default:
        return null;
    }
  };

  return (
    <>
      {isMobileDetail && (
        <button
          type='button'
          onClick={handleBack}
          className='flex items-center justify-start px-4 py-1.5 bg-grayscale-gray01 h-[54px]'
        >
          <div className='flex justify-between w-full'>
            <div className='flex items-center gap-2 typo-title2 text-text-primary'>
              <NavigationLeftIcon />
              {(() => {
                if (invoiceId) return t('Invoice', '인보이스');
                const tabLabel = isBusiness
                  ? BUSINESS_TAB_LABELS[activeMenu] || activeMenu
                  : activeMenu === 'refunds'
                    ? 'Refunds & Cancellations'
                    : activeMenu;
                return t(tabLabel, TAB_LABELS_KR[tabLabel] || tabLabel);
              })()}
            </div>
            {invoiceId && (
              <button
                onClick={handleCopyLink}
                className='flex justify-center items-center size-[26px] text-icon-primary'
              >
                <LinksIcon />
              </button>
            )}
          </div>
        </button>
      )}

      <div
        className={`flex flex-col items-center ${
          isAdmin
            ? 'px-4 pt-6 pb-[220px] tabletH:pt-[60px] tabletH:px-[80px] tabletH:pb-[500px]'
            : !isMobile
              ? 'p-10 pb-[320px]'
              : isMobileDetail
                ? ''
                : 'px-4 pt-6 pb-[220px] gap-10'
        } bg-grayscale-gray01 w-full tabletH:min-h-[1006px] overflow-y-auto`}
      >
        {!isAdmin && !isMobileDetail && <HeaderText isAdmin={isAdmin} />}
        {!isAdmin && isMobile && !isMobileDetail && (
          <div className='w-full'>
            <PersonalInfoProfileImage
              onImageChange={setProfileImageFile}
            />{' '}
          </div>
        )}

        <div
          className={`w-full flex justify-start overflow-hidden ${
            isMobile ? 'flex-col' : 'flex-row'
          } ${
            isAdmin
              ? isInvoice
                ? 'max-w-[1166px]'
                : 'max-w-[1462px]'
              : !isMobile
                ? 'max-w-[1166px]'
                : 'max-w-full'
          } ${
            isMobileDetail
              ? invoiceId
                ? 'p-4'
                : activeMenu === 'My Profile'
                  ? 'pt-4 px-[30px] pb-[160px] gap-[60px]'
                  : activeMenu === 'My Apply' ||
                      activeMenu === 'Account setting' ||
                      activeMenu === 'Payments'
                    ? 'p-4 pb-[160px] gap-[60px]'
                    : activeMenu === 'Activities'
                      ? 'px-4 pb-[160px] gap-[60px]'
                      : activeMenu === 'Coupons'
                        ? 'p-4 pb-[160px] gap-[80px]'
                        : 'pt-4 px-4 pb-[120px]'
              : ''
          }`}
        >
          {isMobile && !isMobileMenu && (
            <div className='w-full'>
              {isAdmin ? adminRenderContent() : renderContent()}
            </div>
          )}
          {!(isMobile && invoiceId) && (
            <div
              className={`w-full ${
                isAdmin
                  ? 'max-w-[298px] min-w-[230px]'
                  : 'tabletH:max-w-[298px] min-w-[184px]'
              } flex-shrink`}
            >
              <MyProfileSidebar
                activeMenu={activeMenu}
                onMenuClick={handleMenuClick}
                isAdmin={isAdmin}
                isBusiness={isBusiness}
              />
            </div>
          )}

          {!isMobile && (
            <div className='w-full'>
              {isAdmin ? adminRenderContent() : renderContent()}
            </div>
          )}
        </div>
        {isMobile && invoiceId && (
          <div className='w-full text-start typo-b12 text-grayscale-gray05 p-4 pt-[60px]'>
            {t('© Koready. All right reserved', '© Koready. 모든 권리 보유')}
          </div>
        )}

        {copyToast}
      </div>
    </>
  );
};

export default MyPage;
